import wx from 'weixin-js-sdk'
import axios from '../http'
import store from '../store'
import Config from '../config/config'

let isWXReady = false
let wxReadyCallbacks = []

/**
 * 初始化微信 JSSDK
 * @param {string} webUrl 当前页面地址（window.location.href）
 * @param {Array} jsApiList 要使用的 API 列表（可选）
 * @param {Function} callback 初始化完成回调（可选）
 */
export const SDKRegister = (webUrl, jsApiList = [], callback) => {
  const url = '/system/jssdk/signature/'
  const params = {
    url: encodeURIComponent(webUrl)
  }

  if (!jsApiList.length) {
    jsApiList = [
      'onMenuShareAppMessage',
      'onMenuShareTimeline',
      'onMenuShareQQ',
      'onMenuShareWeibo',
      'getLocation',
      'scanQRCode',
      'openLocation',
      'requestMerchantTransfer' // 增加转账 API
    ]
  }

  axios.get(`${url}?url=${params.url}`).then(res => {
    const data = res.data

    wx.config({
      debug: false, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
      appId: Config.appId, // 必填，公众号的唯一标识
      timestamp: data.timestamp, // 必填，生成签名的时间戳
      nonceStr: data.nonceStr, // 必填，生成签名的随机串
      signature: data.signature, // 必填，签名，见附录1
      jsApiList
    })

    wx.ready(() => {
      isWXReady = true
      // 执行所有等待中的回调
      wxReadyCallbacks.forEach(cb => cb())
      wxReadyCallbacks = []

      // 获取地理位置
      if (!store.state.location) {
        wx.getLocation({
          type: 'wgs84',
          success(res) {
            const payload = { latitude: res.latitude, longitude: res.longitude }
            store.dispatch('getLocation', payload)
          },
          fail(err) {
            console.log('获取地理位置失败:', err)
            const payload = { latitude: '', longitude: '' }
            store.dispatch('getLocation', payload)
          }
        })
      }

      if (typeof callback === 'function') {
        callback(null)
      }
    })

    wx.error(function(err) {
      console.error('微信 JSSDK 初始化失败:', err)
      if (typeof callback === 'function') {
        callback(err)
      }
    })
  })
}

/**
 * 回调版：调起微信商家转账确认页面
 * @param {Object} config - 配置参数
 *
 *
 * 使用示例
  1. 初始化微信SDK
    SDKRegister(window.location.href, [], (err) => {
      if (err) {
        console.error('SDK 初始化失败', err)
        return
      }

      console.log('SDK 初始化成功')

      // 2. 初始化完成后调用微信商家转账（回调方式）
      invokeWechatTransfer({
        mchId: '1234567890',
        appId: 'wx9999999999999999',
        package: 'prepay_id=your_package_id_here',

        success(res) {
          console.log('转账调用成功:', res)
        },
        fail(err) {
          console.error('转账失败或取消:', err)
        },
        complete(res) {
          console.log('转账操作完成（无论成功或失败）:', res)
        }
      })
    })
 *
 */
export const invokeWechatTransfer = (config) => {
  if (!config.package) {
    const err = { errMsg: 'missing required parameters' }
    console.error(err.errMsg)
    if (typeof config.fail === 'function') {
      config.fail(err)
    }
    return
  }

  const doInvoke = () => {
    wx.checkJsApi({
      jsApiList: ['requestMerchantTransfer'],
      success(checkRes) {
        if (checkRes.checkResult['requestMerchantTransfer']) {
          // eslint-disable-next-line no-undef
          WeixinJSBridge.invoke(
            'requestMerchantTransfer',
            {
              mchId: Config.mchId,
              appId: Config.appId,
              package: config.package
            },
            function(res) {
              if (res.err_msg === 'requestMerchantTransfer:ok') {
                if (typeof config.success === 'function') {
                  config.success(res)
                }
              } else {
                if (typeof config.fail === 'function') {
                  config.fail(res)
                }
              }
              if (typeof config.complete === 'function') {
                config.complete(res)
              }
            }
          )
        } else {
          const err = { errMsg: 'wx version too low' }
          console.error(err.errMsg)
          if (typeof config.fail === 'function') {
            config.fail(err)
          }
          if (typeof config.complete === 'function') {
            config.complete(err)
          }
        }
      },
      fail(err) {
        console.error('checkJsApi失败:', err)
        if (typeof config.fail === 'function') {
          config.fail(err)
        }
        if (typeof config.complete === 'function') {
          config.complete(err)
        }
      }
    })
  }

  if (isWXReady) {
    doInvoke()
  } else {
    wxReadyCallbacks.push(doInvoke)
  }
}

/**
 * Promise：等待微信SDK初始化完成
 */
export const waitForWXReady = () => {
  return new Promise(resolve => {
    if (isWXReady) {
      resolve()
    } else {
      wxReadyCallbacks.push(resolve)
    }
  })
}

//  异步转账调用 使用示例
// const transfer = async () => {
//   try {
//     const result = await invokeWechatTransferAsync({
//       mchId: '1234567890',
//       appId: 'wx9999999999999999',
//       package: 'prepay_id=your_package_id_here'
//     })
//     console.log('转账调起成功:', result)
//   } catch (err) {
//     console.error('转账失败:', err)
//   }
// }

/**
 * Promise：调起微信商家转账确认页面（推荐使用）
 * @param {Object} config - 转账参数
 * @returns {Promise<object>} - 调起结果
 */
export const invokeWechatTransferAsync = async(config) => {
  if (!config.package) {
    const err = { errMsg: 'missing required parameters' }
    console.error(err.errMsg)
    throw err
  }

  await waitForWXReady()

  return new Promise((resolve, reject) => {
    wx.checkJsApi({
      jsApiList: ['requestMerchantTransfer'],
      success(checkRes) {
        console.log('checkJsApi成功:', checkRes)
        if (checkRes.checkResult['requestMerchantTransfer']) {
          // eslint-disable-next-line no-undef
          WeixinJSBridge.invoke(
            'requestMerchantTransfer',
            {
              mchId: Config.mchId,
              appId: Config.appId,
              package: config.package
            },
            function(res) {
              if (res.err_msg === 'requestMerchantTransfer:ok') {
                resolve(res)
              } else {
                reject(res)
              }
            }
          )
        } else {
          const err = { errMsg: 'wx version too low' }
          console.error(err.errMsg)
          reject(err)
        }
      },
      fail(err) {
        console.error('checkJsApi失败:', err)
        reject(err)
      }
    })
  })
}



// WEBPACK FOOTER //
// ./src/util/weixin.js